import { useState } from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { formatInstant } from '@/lib/utils/date';
import { Button } from '@/components/ui/button';
import { activeEvents } from './projection';
import { gaDays, formatGa } from './calculations';
import { trajectoryRange } from './clinicalReferences';
import { focusPrenatalVisit } from './FetalHeartTrends';
import type { ClinicalEvent } from './types';
import type { PracticeConfiguration } from './rules';
export function prenatalTrendData(
  events: ClinicalEvent[],
  type: string,
  keys: string[],
  timeZone: string,
  edd?: string | null,
  baseline?: number | null,
) {
  return activeEvents(events)
    .filter((e) => e.event_type === type)
    .sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at))
    .map((e) => {
      const date = formatInstant(e.occurred_at, 'yyyy-MM-dd', timeZone);
      const days = gaDays(date, edd);
      return {
        eventId: e.id,
        encounterId: e.encounter_id,
        date,
        ga: days === null ? undefined : days / 7,
        gaLabel: formatGa(days),
        ...Object.fromEntries(
          keys.map((k) => [
            k,
            typeof e.payload[k] === 'number'
              ? (k === 'grams' ? (e.payload[k] as number) - (baseline ?? 0) : (e.payload[k] as number)) /
                (k === 'grams' ? 1000 : 1)
              : undefined,
          ]),
        ),
      };
    });
}
export function weightTrajectoryBand(
  configuration: PracticeConfiguration | undefined,
  bmi: number | null,
  fetusCount: number,
  edd: string | null,
) {
  const refs =
    configuration?.clinically_reviewed && configuration.clinical_references?.enabled
      ? configuration.clinical_references
      : undefined;
  if (!refs || bmi === null || !Number.isFinite(bmi) || (!edd || gaDays(edd, edd) === null)) return undefined;
  const trajectory = refs.weight_gain_trajectories?.find(
    (t) => t.fetusCount === fetusCount && bmi >= t.minBmi && (t.maxBmi === null || bmi < t.maxBmi),
  );
  if (!trajectory || trajectory.points.length < 2) return undefined;
  return {
    citation: trajectory.citation,
    points: trajectory.points.map((p) => ({
      ga: p.gaDays / 7,
      reviewedGainKg: trajectoryRange([trajectory], bmi, fetusCount, p.gaDays),
    })),
    startDays: trajectory.points[0].gaDays,
    endDays: trajectory.points.at(-1)!.gaDays,
  };
}
export function PrenatalTrends({
  events,
  timeZone,
  edd = null,
  prePregnancyWeightGrams,
  heightCm,
  configuration,
  fetusCount = 1,
}: {
  events: ClinicalEvent[];
  timeZone: string;
  edd?: string | null;
  prePregnancyWeightGrams?: number | null;
  heightCm?: number | null;
  configuration?: PracticeConfiguration;
  fetusCount?: number;
}) {
  const [show, setShow] = useState(true);
  const refs =
    configuration?.clinically_reviewed && configuration.clinical_references?.enabled
      ? configuration.clinical_references
      : undefined;
  const series = [
    { name: 'Blood pressure (mmHg)', type: 'VITALS.BP', keys: ['systolic', 'diastolic'] },
    {
      name: prePregnancyWeightGrams ? 'Maternal weight gain (kg)' : 'Maternal weight (kg)',
      type: 'VITALS.WEIGHT',
      keys: ['grams'],
    },
    { name: 'Fundal height (cm)', type: 'PRENATAL.FUNDAL_HEIGHT', keys: ['cm'] },
  ];
  const limits = refs?.bmi_limits;
  const kg = (prePregnancyWeightGrams ?? 0) / 1000;
  const bmi =
    limits &&
    heightCm &&
    heightCm >= limits.minHeightCm &&
    heightCm <= limits.maxHeightCm &&
    kg >= limits.minWeightKg &&
    kg <= limits.maxWeightKg
      ? kg / (heightCm / 100) ** 2
      : null;
  const gainBand = weightTrajectoryBand(configuration, bmi, fetusCount, edd);
  const target =
    bmi === null
      ? undefined
      : (fetusCount === 1 ? refs?.singleton_gain_bands : fetusCount === 2 ? refs?.twin_gain_bands : undefined)?.find(
          (b) => bmi >= b.minBmi && (b.maxBmi === null || bmi < b.maxBmi),
        );
  const urine = activeEvents(events).filter((e) => e.event_type === 'LAB.URINE_DIP' && e.payload.protein !== undefined);
  return (
    <div className="space-y-5">
      <Button type="button" variant="outline" aria-expanded={show} onClick={() => setShow((v) => !v)}>
        {show ? 'Hide maternal trend charts' : 'Show maternal trend charts'}
      </Button>
      {show && (
        <>
          <p className="text-sm text-muted-foreground">
            {edd
              ? 'Horizontal axis: gestational age in weeks.'
              : 'EDD not set: dates shown; gestational age unavailable.'}{' '}
            Select a documented value to open its visit.
          </p>
          <div className="grid gap-6 lg:grid-cols-3">
            {series.map((series) => {
              const data = prenatalTrendData(events, series.type, series.keys, timeZone, edd, prePregnancyWeightGrams);
              const band = series.type === 'VITALS.WEIGHT' ? gainBand : undefined;
              return (
                <section key={series.type} aria-label={series.name}>
                  <h3>{series.name}</h3>
                  {!data.length ? (
                    <p>No confirmed observations.</p>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={210}>
                        <ComposedChart
                          data={band ? [...data, ...band.points].sort((a, b) => (a.ga ?? 0) - (b.ga ?? 0)) : data}
                          onClick={(state) => {
                            const point = state?.activePayload?.find((item) => item.payload?.encounterId)?.payload;
                            if (point?.encounterId) focusPrenatalVisit(point.encounterId);
                          }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey={edd ? 'ga' : 'date'}
                            type={edd ? 'number' : 'category'}
                            domain={['auto', 'auto']}
                          />
                          <YAxis domain={['auto', 'auto']} />
                          <Tooltip />
                          {band && (
                            <Area
                              data={band.points}
                              dataKey="reviewedGainKg"
                              type="linear"
                              name="Reviewed weight gain range (kg)"
                              stroke="var(--chart-2)"
                              fill="var(--chart-2)"
                              fillOpacity={0.18}
                              connectNulls={false}
                              isAnimationActive={false}
                              activeDot={false}
                            />
                          )}
                          {refs &&
                            series.type === 'VITALS.BP' &&
                            Object.entries(refs.bp_reference).map(([key, value]) => (
                              <ReferenceLine key={key} y={value} strokeDasharray="4 4" label={key} />
                            ))}
                          {refs && series.type === 'PRENATAL.FUNDAL_HEIGHT' && (
                            <>
                              <ReferenceArea
                                x1={refs.fh_window.minDays / 7}
                                x2={refs.fh_window.maxDays / 7}
                                fill="var(--chart-2)"
                                fillOpacity={0.08}
                              />
                              <ReferenceLine
                                segment={[
                                  { x: refs.fh_window.minDays / 7, y: refs.fh_window.minDays / 7 },
                                  { x: refs.fh_window.maxDays / 7, y: refs.fh_window.maxDays / 7 },
                                ]}
                                strokeDasharray="4 4"
                              />
                              {[-1, 1].map((sign) => (
                                <ReferenceLine
                                  key={sign}
                                  segment={[
                                    {
                                      x: refs.fh_window.minDays / 7,
                                      y: refs.fh_window.minDays / 7 + sign * refs.fh_window.toleranceCm,
                                    },
                                    {
                                      x: refs.fh_window.maxDays / 7,
                                      y: refs.fh_window.maxDays / 7 + sign * refs.fh_window.toleranceCm,
                                    },
                                  ]}
                                  strokeDasharray="2 4"
                                />
                              ))}
                            </>
                          )}
                          {series.keys.map((key, i) => (
                            <Line
                              key={key}
                              data={data}
                              dataKey={key}
                              name={key === 'grams' ? 'kg' : key}
                              stroke={i ? 'var(--chart-2)' : 'var(--chart-1)'}
                              isAnimationActive={false}
                              connectNulls={false}
                            />
                          ))}
                        </ComposedChart>
                      </ResponsiveContainer>
                      <details>
                        <summary>Documented values</summary>
                        {data.map((point) => (
                          <Button
                            key={point.eventId}
                            variant="ghost"
                            className="h-auto min-h-11 whitespace-normal"
                            onClick={() => focusPrenatalVisit(point.encounterId)}>
                            {point.date} · {point.gaLabel} ·{' '}
                            {series.keys
                              .map(
                                (k) =>
                                  `${k === 'grams' ? 'kg' : k}: ${point[k as keyof typeof point] ?? 'not documented'}`,
                              )
                              .join(' / ')}
                          </Button>
                        ))}
                      </details>
                    </>
                  )}
                  {series.type === 'VITALS.WEIGHT' && target && (
                    <p>
                      Total pregnancy target: {target.lowLb}–{target.highLb} lb.
                    </p>
                  )}
                  {series.type === 'VITALS.WEIGHT' &&
                    (band ? (
                      <p className="text-sm">
                        Reviewed trajectory from {formatGa(band.startDays)} to {formatGa(band.endDays)}; no band outside
                        this interval. Source: {band.citation}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        A reviewed trajectory matching this pregnancy’s BMI, fetus count and dating is not available.
                      </p>
                    ))}
                  {refs && <p className="text-xs text-muted-foreground">Reference: {refs.citation}</p>}
                </section>
              );
            })}
          </div>
          <section aria-label="Urine protein strip">
            <h3>Urine protein · documented categories</h3>
            <div className="flex flex-wrap gap-2">
              {urine.map((e) => (
                <Button
                  key={e.id}
                  variant="outline"
                  className="h-auto min-h-11 flex-col"
                  onClick={() => focusPrenatalVisit(e.encounter_id)}>
                  <span>
                    {formatInstant(e.occurred_at, 'MMM d, yyyy', timeZone)} ·{' '}
                    {formatGa(gaDays(formatInstant(e.occurred_at, 'yyyy-MM-dd', timeZone), edd))}
                  </span>
                  <span>{String(e.payload.protein)}</span>
                </Button>
              ))}
            </div>
            {!urine.length && <p>Not documented.</p>}
          </section>
        </>
      )}
    </div>
  );
}
